import { useState } from 'react';
import { ApiError, apiBlob } from '@/api/client';
import { artifactDownloadUrl } from '@/api/endpoints';
import type { Artifact } from '@/api/schemas';
import { saveBlob } from '@/lib/download';
import { showErrorToast } from '@/lib/toasts';

/** What the failed download toast says: the API's own title and code, or a generic line naming the file. */
const downloadError = (e: unknown, filename: string) => (e instanceof ApiError ? { title: e.toastTitle, description: `${e.code} · ${e.message}` } : { title: `Could not download ${filename}` });

/**
 * Two kinds of download link (api/openapi.yaml `PresignedUrl`): a presigned S3 URL is cross-origin and must never see
 * the bearer token, so it opens directly; the API's own route (a local Hangar) is fetched with the token in the header
 * and saved as a file.
 */
export function useArtifactDownload() {
  const [busy, setBusy] = useState<ReadonlySet<string>>(() => new Set());
  const download = async (a: Artifact) => {
    setBusy((s) => new Set(s).add(a.id));
    try {
      const link = await artifactDownloadUrl(a.id);
      if (link.requiresAuth) saveBlob(await apiBlob(link.url), a.filename);
      else window.open(link.url, '_blank', 'noopener');
    } catch (e) {
      showErrorToast(downloadError(e, a.filename));
    } finally {
      setBusy((s) => {
        const next = new Set(s);
        next.delete(a.id);
        return next;
      });
    }
  };
  return { busy, download };
}
